import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Search, MapPin, CheckCircle2, PlusCircle, XCircle, Loader2, ArrowRight } from 'lucide-react';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { toast } from 'sonner';
import { registerDynamicCities, SEARCH_RADIUS_KM } from '../lib/cities-data';
import { logError } from '../lib/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const DEBOUNCE_MS = 350;
const fmtKm = (km) => `${String(km).replace('.', ',')} km`;

/** Debounced commune lookup against the backend (ignores stale responses). */
function useCommuneSearch(query) {
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const timer = useRef(null);
  const lastQuery = useRef('');

  useEffect(() => {
    clearTimeout(timer.current);
    const q = query.trim();
    if (q.length < 2) { setResults(null); setError(null); return undefined; }
    timer.current = setTimeout(async () => {
      lastQuery.current = q;
      setSearching(true);
      setError(null);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/communes/search`, { params: { q } });
        if (lastQuery.current === q) setResults(res.data.items || []);
      } catch (err) {
        logError('recherche commune', err);
        if (lastQuery.current === q) setError(err?.response?.data?.detail || 'Recherche indisponible, réessayez.');
      } finally {
        if (lastQuery.current === q) setSearching(false);
      }
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer.current);
  }, [query]);

  return { results, searching, error };
}

/** Action button for one commune: view the page, add the commune, or "out of zone". */
function CommuneAction({ commune, adding, onView, onAdd }) {
  if (commune.served) {
    return (
      <Button size="sm" variant="outline" onClick={() => onView(commune.path)} data-testid="city-search-view">
        <CheckCircle2 className="h-4 w-4 mr-1.5 text-[hsl(var(--primary))]" /> Desservie <ArrowRight className="h-4 w-4 ml-1.5" />
      </Button>
    );
  }
  if (commune.eligible) {
    const busy = adding === commune.code;
    return (
      <Button size="sm" className="bg-[hsl(var(--accent))] hover:bg-[hsl(var(--accent))]/90 text-white" onClick={() => onAdd(commune)} disabled={busy} data-testid="city-search-add">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><PlusCircle className="h-4 w-4 mr-1.5" /> Ajouter ma commune</>}
      </Button>
    );
  }
  return <Badge variant="secondary" className="text-[11px] whitespace-nowrap" data-testid="city-search-out-of-zone"><XCircle className="h-3.5 w-3.5 mr-1" /> Hors zone</Badge>;
}

function CommuneResults({ query, results, adding, onView, onAdd }) {
  if (!results) return null;
  return (
    <Card className="mt-3 divide-y divide-border overflow-hidden" data-testid="city-search-results">
      {results.length === 0 && (
        <p className="p-4 text-sm text-muted-foreground" data-testid="city-search-empty">Aucune commune trouvée pour « {query} ». Vérifiez l'orthographe ou appelez-nous.</p>
      )}
      {results.map((c) => (
        <div key={c.code} className="flex items-center justify-between gap-3 p-3 sm:p-4" data-testid="city-search-result" data-eligible={c.eligible} data-served={c.served}>
          <div className="min-w-0">
            <p className="font-medium truncate">{c.name} <span className="text-muted-foreground font-normal">{c.cp}</span></p>
            <p className="text-xs text-muted-foreground">{c.departement} · à {fmtKm(c.distance_km)} de Servins</p>
          </div>
          <CommuneAction commune={c} adding={adding} onView={onView} onAdd={onAdd} />
        </div>
      ))}
    </Card>
  );
}

/**
 * Public search: type a town or village name, see if it is within 30 km of Servins and add it
 * in one click — its 4 local pages (services, ménage, jardinage, taille de haies) go live instantly.
 */
export const CitySearch = ({ compact = false, onAdded }) => {
  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(null);
  const navigate = useNavigate();
  const { results, searching, error } = useCommuneSearch(query);

  const add = async (c) => {
    setAdding(c.code);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/communes/add`, { code: c.code });
      if (!res.data.already) {
        registerDynamicCities([res.data]);
        toast.success(`${c.name} ajoutée : nos Easers s'y déplacent ! Découvrez la page.`);
        onAdded?.(res.data);
      }
      navigate(res.data.path);
    } catch (err) {
      logError('ajout commune', err);
      toast.error(err?.response?.data?.detail || "Impossible d'ajouter cette commune");
    } finally {
      setAdding(null);
    }
  };

  return (
    <div className={compact ? '' : 'max-w-2xl mx-auto'} data-testid="city-search">
      <div className="relative">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground pointer-events-none" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Votre ville ou village (ex. Gouy-en-Artois, Camblain…)"
          className="h-12 pl-11 pr-11 text-base bg-card shadow-sm"
          aria-label="Rechercher votre commune"
          data-testid="city-search-input"
        />
        {searching && <Loader2 className="absolute right-3.5 top-1/2 -translate-y-1/2 h-5 w-5 animate-spin text-[hsl(var(--primary))]" data-testid="city-search-loading" />}
      </div>
      <p className="text-xs text-muted-foreground mt-2 flex items-center gap-1.5"><MapPin className="h-3.5 w-3.5" /> Toutes les communes — villes <strong>et villages</strong> — situées à moins de {SEARCH_RADIUS_KM} km de Servins sont desservies.</p>

      {error && <p className="mt-3 text-sm text-destructive" data-testid="city-search-error">{error}</p>}

      <CommuneResults query={query} results={results} adding={adding} onView={navigate} onAdd={add} />
    </div>
  );
};

/** Placeholder shown while the dynamic communes list is being fetched for an unknown slug. */
export const CityPageSkeleton = () => (
  <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16 space-y-6" data-testid="city-page-skeleton">
    <Skeleton className="h-6 w-40" />
    <Skeleton className="h-12 w-3/4" />
    <Skeleton className="h-5 w-full" />
    <Skeleton className="h-5 w-5/6" />
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 pt-4">
      {[0, 1, 2].map((i) => <Skeleton key={`city-skel-${i}`} className="h-40 rounded-xl" />)}
    </div>
  </div>
);
